/**
 * Custom export formats for the Forecast V3 flat exports — the budget pack's
 * "Export" tab and the "Raw data export · CSV". An organization saves a
 * layout (org-config `budgetExportFormats`, optionally overridden per
 * location); with none saved, the built-in Lodgeic layouts render exactly as
 * before (buildExportSheetRows / buildForecastRawExport).
 *
 * A format is data, not code: an ordered column list drawn from a fixed
 * catalog, a month-header template, a sign convention per section, and which
 * row families to emit. The rows come from the same inputs the built-in
 * exports read, so a custom layout can never disagree with the grid on a
 * number — only on how it is laid out and signed.
 */

import * as XLSX from 'xlsx';
import { MONTHS } from '../types';
import type { ForecastAccount } from '../types';
import { resolveStatAccountMonths } from './forecastRawExport';
import type { RawExportDepartment, StatLeaf } from './forecastRawExport';
import type { BuildBudgetPackInput } from './budgetPackExport';

export type ExportFormatTarget = 'packExport' | 'raw';

export type ExportColumnKey =
  | 'location' | 'accountNumber' | 'accountName' | 'lodgeicAccountId' | 'department'
  | 'accountType' | 'section' | 'months' | 'total' | 'static' | 'year';

export interface ExportColumn {
  key: ExportColumnKey;
  /** Header text; defaults to the catalog header. Ignored for `months` (see monthHeader). */
  header?: string;
  /** `static` columns only — the value written on every row ({year} expands). */
  value?: string;
}

export type ExportSign = 'positive' | 'negative';

export interface BudgetExportFormat {
  name?: string;
  columns: ExportColumn[];
  /** Month header template: {n} 1–12, {mm} 01–12, {mmm} Jan, {mmmm} January, {yyyy}, {yy}, {kind} Actual/Budget/Forecast. A bare {n} writes numeric headers. */
  monthHeader: string;
  /** 'positive' = as the grid shows it (expenses positive); 'negative' = sign flipped. */
  expenseSign: ExportSign;
  revenueSign: ExportSign;
  /** Stat-account rows (room nights, hours, …) after the money accounts. */
  statistics: boolean;
  /** Net Income / Revenue / Expense / department total rows (statement order only). */
  subtotals: boolean;
  /** Every P&L account on the chart the plan doesn't carry, as a row of zeros. */
  chartZeroFill: boolean;
  omitZeroRows: boolean;
  sort: 'statement' | 'accountNumber';
  /** Excel number format for value cells (pack Export tab only). */
  numberFormat?: '0.00' | '#,##0.00' | '0' | 'General';
  /** Pack Export tab name; defaults to "Export". */
  sheetName?: string;
  updatedAt?: string;
  updatedBy?: string;
}

/** One target's saved slots, as GET /forecast/v3/exportFormats returns them. */
export interface ExportFormatSlots {
  org: BudgetExportFormat | null;
  location: BudgetExportFormat | null;
}

export const EXPORT_TARGETS: Record<ExportFormatTarget, { label: string; description: string }> = {
  packExport: { label: 'Budget pack · Export tab', description: 'The flat import tab at the end of the budget pack workbook' },
  raw: { label: 'Raw data export · CSV', description: 'The flat CSV from the grid’s export menu' },
};

export const COLUMN_CATALOG: Record<ExportColumnKey, { label: string; header: string; description: string }> = {
  location: { label: 'Location', header: 'Location', description: 'Property name' },
  accountNumber: { label: 'Account number', header: 'Account Number', description: 'GL account number' },
  accountName: { label: 'Account name', header: 'Account Name', description: 'Account name (the label on subtotal rows)' },
  lodgeicAccountId: { label: 'Lodgeic account ID', header: 'Lodgeic Account ID', description: 'Internal chart-of-account id' },
  department: { label: 'Department', header: 'Department', description: 'Department name' },
  accountType: { label: 'Account type', header: 'Account Type', description: 'Revenue / Expense / Statistics' },
  section: { label: 'Section', header: 'Section', description: 'Revenue, Expense or Statistics group' },
  months: { label: 'Months (12 columns)', header: '', description: 'One value column per month' },
  total: { label: 'Total', header: 'Total', description: 'Sum of the twelve months' },
  static: { label: 'Fixed value', header: 'Column', description: 'The same text on every row (e.g. a budget code)' },
  year: { label: 'Year', header: 'Year', description: 'The version year' },
};

export const MONTH_HEADER_PRESETS: { value: string; label: string }[] = [
  { value: '{mmm} {yyyy}', label: 'Jan 2027' },
  { value: '{n}', label: '1, 2 … 12' },
  { value: '{mmm}', label: 'Jan' },
  { value: '{mmmm}', label: 'January' },
  { value: '{yyyy}-{mm}', label: '2027-01' },
  { value: '{mm}/{yyyy}', label: '01/2027' },
  { value: '{mmm} {yyyy} {kind}', label: 'Jan 2027 Budget' },
];

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** The built-in layouts expressed as formats — a starting point in the editor. */
export function standardFormat(target: ExportFormatTarget): BudgetExportFormat {
  if (target === 'raw') {
    return {
      name: 'Lodgeic standard',
      columns: [
        { key: 'location' }, { key: 'accountName', header: 'Account' }, { key: 'lodgeicAccountId' },
        { key: 'accountNumber' }, { key: 'department' }, { key: 'months' }, { key: 'total' },
      ],
      monthHeader: '{mmm} {yyyy} {kind}',
      expenseSign: 'positive',
      revenueSign: 'positive',
      statistics: true,
      subtotals: true,
      chartZeroFill: false,
      omitZeroRows: false,
      sort: 'statement',
    };
  }
  return {
    name: 'Lodgeic standard',
    columns: [
      { key: 'location' }, { key: 'accountNumber' }, { key: 'accountName' }, { key: 'department' },
      { key: 'accountType' }, { key: 'section' }, { key: 'months' }, { key: 'total' },
    ],
    monthHeader: '{mmm} {yyyy}',
    expenseSign: 'negative',
    revenueSign: 'positive',
    statistics: true,
    subtotals: false,
    chartZeroFill: true,
    omitZeroRows: false,
    sort: 'statement',
    numberFormat: '0.00',
  };
}

/** Account number × month number, everything positive — the common GL budget-import shape. */
export function glImportFormat(): BudgetExportFormat {
  return {
    name: 'GL import (account × month number)',
    columns: [{ key: 'accountNumber', header: 'Account Number' }, { key: 'months' }, { key: 'total', header: 'Total' }],
    monthHeader: '{n}',
    expenseSign: 'positive',
    revenueSign: 'positive',
    statistics: true,
    subtotals: false,
    chartZeroFill: true,
    omitZeroRows: false,
    sort: 'statement',
    numberFormat: '0.00',
  };
}

/** Location override → org default → null (built-in layout). */
export function resolveExportFormat(slots?: ExportFormatSlots | null): { format: BudgetExportFormat | null; source: 'location' | 'org' | 'standard' } {
  if (slots?.location) return { format: slots.location, source: 'location' };
  if (slots?.org) return { format: slots.org, source: 'org' };
  return { format: null, source: 'standard' };
}

export function monthHeaderCell(template: string, monthIndex: number, year: number, kind: string): string | number {
  const t = String(template ?? '').trim() || '{mmm} {yyyy}';
  if (t === '{n}') return monthIndex + 1;
  return t
    .replace(/\{mmmm\}/g, MONTH_LONG[monthIndex])
    .replace(/\{mmm\}/g, MONTH_SHORT[monthIndex])
    .replace(/\{mm\}/g, String(monthIndex + 1).padStart(2, '0'))
    .replace(/\{n\}/g, String(monthIndex + 1))
    .replace(/\{yyyy\}/g, String(year))
    .replace(/\{yy\}/g, String(year).slice(-2))
    .replace(/\{kind\}/g, kind);
}

type Cell = string | number;

interface Line {
  kind: 'account' | 'subtotal' | 'stat';
  section: string;
  accountType: string;
  accountNumber: string;
  accountName: string;
  lodgeicAccountId: number | '';
  department: string;
  months: number[];
}

const round2 = (v: number) => Math.round(v * 100) / 100;
// `|| 0` keeps a negated zero from exporting as -0.
const signed = (v: number | null | undefined, sign: 1 | -1) => round2(sign * (Number(v ?? 0) || 0)) || 0;
const byNumber = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true });

function collectLines(input: BuildBudgetPackInput, format: BudgetExportFormat): Line[] {
  const revSign: 1 | -1 = format.revenueSign === 'negative' ? -1 : 1;
  const expSign: 1 | -1 = format.expenseSign === 'negative' ? -1 : 1;
  const subtotals = format.subtotals && format.sort === 'statement';
  const lines: Line[] = [];
  const monthsOf = (rec: Record<string, number> | undefined, sign: 1 | -1) => MONTHS.map((m) => signed(rec?.[m], sign));
  const subtotal = (label: string, section: string, rec: Record<string, number> | undefined, sign: 1 | -1, department = '') =>
    lines.push({ kind: 'subtotal', section, accountType: '', accountNumber: '', accountName: label, lodgeicAccountId: '', department, months: monthsOf(rec, sign) });
  const accountLine = (a: ForecastAccount, dept: RawExportDepartment, section: 'Revenue' | 'Expense', sign: 1 | -1) =>
    lines.push({
      kind: 'account', section, accountType: a.accountType || section,
      accountNumber: a.accountNumber || '', accountName: a.accountName || '', lodgeicAccountId: a.chartOfAccountId ?? '',
      department: dept.department || a.department || '', months: monthsOf(a.monthlyValues as Record<string, number>, sign),
    });

  if (subtotals) subtotal('Net Income', 'Net Income', input.netIncome, 1);
  const pushSection = (depts: RawExportDepartment[], section: 'Revenue' | 'Expense', totals: Record<string, number>, sign: 1 | -1) => {
    if (subtotals) subtotal(section, section, totals, sign);
    depts.forEach((dept) => {
      if (subtotals) subtotal(dept.department, section, dept.monthlyTotals, sign, dept.department);
      dept.accounts.forEach((a) => accountLine(a, dept, section, sign));
    });
  };
  pushSection(input.revenueDepartments, 'Revenue', input.revenueTotals, revSign);
  pushSection(input.expenseDepartments, 'Expense', input.expenseTotals, expSign);

  if (format.chartZeroFill) {
    const emitted = new Set<number>();
    [...input.revenueDepartments, ...input.expenseDepartments].forEach((d) => d.accounts.forEach((a) => emitted.add(a.chartOfAccountId)));
    const revenueTypes = new Set(['revenue', 'other income']);
    const expenseTypes = new Set(['expense', 'other expense', 'cost of goods sold']);
    (input.chartOfAccounts || [])
      .filter((a) => !emitted.has(a.id))
      .filter((a) => { const t = String(a.accountType || '').trim().toLowerCase(); return revenueTypes.has(t) || expenseTypes.has(t); })
      .sort((a, b) => byNumber(String(a.accountNumber || ''), String(b.accountNumber || '')))
      .forEach((a) => {
        const section = revenueTypes.has(String(a.accountType || '').trim().toLowerCase()) ? 'Revenue' : 'Expense';
        lines.push({
          kind: 'account', section, accountType: a.accountType || section, accountNumber: a.accountNumber || '',
          accountName: a.accountName || '', lodgeicAccountId: a.id, department: a.department || '', months: MONTHS.map(() => 0),
        });
      });
  }

  const hasStats = !!input.roomStats || (input.statAccounts?.length ?? 0) > 0;
  if (format.statistics && hasStats) {
    const resolved = resolveStatAccountMonths(input);
    const pushLeaves = (leaves: StatLeaf[], group: string, totalLabel = `Total ${group}`) => {
      const sorted = [...leaves].sort((a, b) => byNumber(a.account.accountNumber, b.account.accountNumber) || a.account.accountName.localeCompare(b.account.accountName));
      if (subtotals && sorted.length > 0) {
        lines.push({
          kind: 'subtotal', section: 'Statistics', accountType: '', accountNumber: '', accountName: totalLabel, lodgeicAccountId: '', department: '',
          months: MONTHS.map((_, i) => round2(sorted.reduce((s, l) => s + (Number(l.months[i]) || 0), 0))),
        });
      }
      sorted.forEach((leaf) => lines.push({
        kind: 'stat', section: `Statistics · ${group}`, accountType: 'Statistics', accountNumber: leaf.account.accountNumber || '',
        accountName: leaf.account.accountName || '', lodgeicAccountId: leaf.account.chartOfAccountId ?? '',
        department: leaf.account.department || '', months: leaf.months.map((v) => signed(v, 1)),
      }));
    };
    pushLeaves(resolved.occupiedLeaves, 'Occupied Rooms');
    pushLeaves(resolved.availableLeaves, 'Available Rooms');
    Array.from(resolved.otherMapped.keys()).sort((a, b) => a.localeCompare(b)).forEach((type) => pushLeaves(resolved.otherMapped.get(type)!, type));
    pushLeaves(resolved.unmapped, 'Other', 'Other Statistics');
  }

  let out = format.omitZeroRows ? lines.filter((l) => l.months.some((v) => v !== 0)) : lines;
  if (format.sort === 'accountNumber') {
    out = out.filter((l) => l.kind !== 'subtotal').sort((a, b) => byNumber(a.accountNumber, b.accountNumber) || a.accountName.localeCompare(b.accountName));
  }
  return out;
}

/** Header row first, then one row per line — ready for a CSV writer or aoa_to_sheet. */
export function buildFormattedExportRows(input: BuildBudgetPackInput, format: BudgetExportFormat): Cell[][] {
  const { locationName, year } = input;
  const kindOf = (i: number) => (input.actualMonthKeys?.has(MONTHS[i]) ? 'Actual' : (input.planLabel || 'Budget'));
  const expandStatic = (v?: string) => String(v ?? '').replace(/\{yyyy\}|\{year\}/g, String(year));
  const columns = format.columns.length ? format.columns : standardFormat('packExport').columns;

  const header: Cell[] = [];
  columns.forEach((c) => {
    if (c.key === 'months') MONTHS.forEach((_, i) => header.push(monthHeaderCell(format.monthHeader, i, year, kindOf(i))));
    else header.push(c.header ?? COLUMN_CATALOG[c.key].header);
  });

  const rows: Cell[][] = [header];
  collectLines(input, format).forEach((line) => {
    const row: Cell[] = [];
    columns.forEach((c) => {
      switch (c.key) {
        case 'location': row.push(locationName); break;
        case 'accountNumber': row.push(line.accountNumber); break;
        case 'accountName': row.push(line.accountName); break;
        case 'lodgeicAccountId': row.push(line.lodgeicAccountId); break;
        case 'department': row.push(line.department); break;
        case 'accountType': row.push(line.accountType); break;
        case 'section': row.push(line.section); break;
        case 'months': row.push(...line.months); break;
        case 'total': row.push(round2(line.months.reduce((s, v) => s + v, 0)) || 0); break;
        case 'static': row.push(expandStatic(c.value)); break;
        case 'year': row.push(year); break;
        default: row.push('');
      }
    });
    rows.push(row);
  });
  return rows;
}

const COLUMN_WIDTHS: Partial<Record<ExportColumnKey, number>> = {
  location: 28, accountNumber: 16, accountName: 40, lodgeicAccountId: 12, department: 22, accountType: 14, section: 16, total: 14, static: 16, year: 8,
};

/** A custom-format Export tab: header row 1, numbers as numbers in the format's number format. */
export function buildFormattedExportSheet(input: BuildBudgetPackInput, format: BudgetExportFormat): XLSX.WorkSheet {
  const aoa = buildFormattedExportRows(input, format);
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = format.columns.flatMap((c) =>
    c.key === 'months' ? MONTHS.map(() => ({ wch: 12 })) : [{ wch: COLUMN_WIDTHS[c.key] ?? 14 }]
  );
  const z = format.numberFormat && format.numberFormat !== 'General' ? format.numberFormat : null;
  if (z) {
    for (let r = 1; r < aoa.length; r++) {
      for (let c = 0; c < aoa[r].length; c++) {
        const cell = ws[XLSX.utils.encode_cell({ r, c })];
        if (cell && cell.t === 'n' && format.columns.length && isValueColumn(format, c)) cell.z = z;
      }
    }
  }
  return ws;
}

/** True when sheet column `c` is a month or Total column (ids and years keep General). */
function isValueColumn(format: BudgetExportFormat, c: number): boolean {
  let at = 0;
  for (const col of format.columns) {
    const width = col.key === 'months' ? MONTHS.length : 1;
    if (c < at + width) return col.key === 'months' || col.key === 'total';
    at += width;
  }
  return false;
}
